/**
 * Dependency-free SVG path optimizer created by OpenAI Codex given https://github.com/svg/svgo as context.
 */

type ReadNumberState = "none" | "sign" | "whole" | "decimal_point" | "decimal" | "e" | "exponent_sign" | "exponent";

type Point = [number, number];

type PathDataCommand =
  | "M"
  | "m"
  | "Z"
  | "z"
  | "L"
  | "l"
  | "H"
  | "h"
  | "V"
  | "v"
  | "C"
  | "c"
  | "S"
  | "s"
  | "Q"
  | "q"
  | "T"
  | "t"
  | "A"
  | "a";

interface PathDataItem {
  command: PathDataCommand;
  args: number[];
  base?: Point;
  coords?: Point;
  sdata?: number[];
}

interface MakeArcs {
  threshold: number;
  tolerance: number;
}

interface FilterContext {
  isSafeToUseZ: boolean;
  maybeHasStrokeAndLinecap: boolean;
  hasMarkerMid: boolean;
}

type InternalParams = {
  makeArcs: MakeArcs | false;
  straightCurves: boolean;
  convertToQ: boolean;
  lineShorthands: boolean;
  convertToZ: boolean;
  curveSmoothShorthands: boolean;
  floatPrecision: number | false;
  smartArcRounding: boolean;
  removeUseless: boolean;
  collapseRepeated: boolean;
  utilizeAbsolute: boolean;
  leadingZero: boolean;
  negativeExtraSpace: boolean;
  noSpaceAfterFlags: boolean;
  forceAbsolutePath: boolean;
};

const DEFAULT_PARAMS: InternalParams = {
  makeArcs: {
    threshold: 2.5,
    tolerance: 0.5,
  },
  straightCurves: true,
  convertToQ: true,
  lineShorthands: true,
  convertToZ: true,
  curveSmoothShorthands: true,
  floatPrecision: 3,
  smartArcRounding: true,
  removeUseless: true,
  collapseRepeated: true,
  utilizeAbsolute: true,
  leadingZero: true,
  negativeExtraSpace: true,
  noSpaceAfterFlags: false,
  forceAbsolutePath: false,
};

let roundData: (data: number[]) => number[] = data => data;
let precision: number | false = DEFAULT_PARAMS.floatPrecision;
let error = 1e-2;
let arcThreshold = DEFAULT_PARAMS.makeArcs ? DEFAULT_PARAMS.makeArcs.threshold : 2.5;
let arcTolerance = DEFAULT_PARAMS.makeArcs ? DEFAULT_PARAMS.makeArcs.tolerance : 0.5;

/**
 * Minify a single SVG path data string using the default convertPathData rules.
 */
export function optimizeSVGPath(pathData: string): string {
  const parsed = parsePathData(pathData);
  if (parsed.length === 0) {
    return pathData;
  }
  const data: PathDataItem[] = parsed.map((item, index) => ({
    command: index === 0 && item.command === "m" ? "M" : item.command,
    args: item.args.slice(),
  }));
  const params = { ...DEFAULT_PARAMS };
  precision = params.floatPrecision;
  error = typeof precision === "number" ? +Math.pow(0.1, precision).toFixed(precision) : 1e-2;
  roundData = typeof precision === "number" && precision > 0 && precision < 20 ? strongRound : round;
  if (params.makeArcs) {
    arcThreshold = params.makeArcs.threshold;
    arcTolerance = params.makeArcs.tolerance;
  }
  convertToRelative(data);
  const filtered = filters(data, params, {
    isSafeToUseZ: true,
    maybeHasStrokeAndLinecap: false,
    hasMarkerMid: false,
  });
  const finalPath = params.utilizeAbsolute && filtered.length ? convertToMixed(filtered, params) : filtered;
  return stringifyOptimizedPath(finalPath, params);
}

/**
 * Convert absolute path data coordinates to relative.
 */
const convertToRelative = (pathData: PathDataItem[]): PathDataItem[] => {
  const start: Point = [0, 0];
  const cursor: Point = [0, 0];
  let prevCoords: Point = [0, 0];

  for (let i = 0; i < pathData.length; i += 1) {
    const pathItem = pathData[i];
    const args = pathItem.args;
    let command = pathItem.command;

    if (command === "m") {
      cursor[0] += args[0];
      cursor[1] += args[1];
      start[0] = cursor[0];
      start[1] = cursor[1];
    } else if (command === "M") {
      if (i !== 0) {
        command = "m";
      }
      args[0] -= cursor[0];
      args[1] -= cursor[1];
      cursor[0] += args[0];
      cursor[1] += args[1];
      start[0] = cursor[0];
      start[1] = cursor[1];
    } else if (command === "l") {
      cursor[0] += args[0];
      cursor[1] += args[1];
    } else if (command === "L") {
      command = "l";
      args[0] -= cursor[0];
      args[1] -= cursor[1];
      cursor[0] += args[0];
      cursor[1] += args[1];
    } else if (command === "h") {
      cursor[0] += args[0];
    } else if (command === "H") {
      command = "h";
      args[0] -= cursor[0];
      cursor[0] += args[0];
    } else if (command === "v") {
      cursor[1] += args[0];
    } else if (command === "V") {
      command = "v";
      args[0] -= cursor[1];
      cursor[1] += args[0];
    } else if (command === "c") {
      cursor[0] += args[4];
      cursor[1] += args[5];
    } else if (command === "C") {
      command = "c";
      args[0] -= cursor[0];
      args[1] -= cursor[1];
      args[2] -= cursor[0];
      args[3] -= cursor[1];
      args[4] -= cursor[0];
      args[5] -= cursor[1];
      cursor[0] += args[4];
      cursor[1] += args[5];
    } else if (command === "s") {
      cursor[0] += args[2];
      cursor[1] += args[3];
    } else if (command === "S") {
      command = "s";
      args[0] -= cursor[0];
      args[1] -= cursor[1];
      args[2] -= cursor[0];
      args[3] -= cursor[1];
      cursor[0] += args[2];
      cursor[1] += args[3];
    } else if (command === "q") {
      cursor[0] += args[2];
      cursor[1] += args[3];
    } else if (command === "Q") {
      command = "q";
      args[0] -= cursor[0];
      args[1] -= cursor[1];
      args[2] -= cursor[0];
      args[3] -= cursor[1];
      cursor[0] += args[2];
      cursor[1] += args[3];
    } else if (command === "t") {
      cursor[0] += args[0];
      cursor[1] += args[1];
    } else if (command === "T") {
      command = "t";
      args[0] -= cursor[0];
      args[1] -= cursor[1];
      cursor[0] += args[0];
      cursor[1] += args[1];
    } else if (command === "a") {
      cursor[0] += args[5];
      cursor[1] += args[6];
    } else if (command === "A") {
      command = "a";
      args[5] -= cursor[0];
      args[6] -= cursor[1];
      cursor[0] += args[5];
      cursor[1] += args[6];
    } else if (command === "Z" || command === "z") {
      cursor[0] = start[0];
      cursor[1] = start[1];
    }

    pathItem.command = command;
    pathItem.args = args;
    pathItem.base = prevCoords;
    pathItem.coords = [cursor[0], cursor[1]];
    prevCoords = pathItem.coords;
  }

  return pathData;
};

/**
 * Main filters loop from convertPathData.
 */
function filters(
  path: PathDataItem[],
  params: InternalParams,
  { isSafeToUseZ, maybeHasStrokeAndLinecap, hasMarkerMid }: FilterContext,
): PathDataItem[] {
  const stringify = data2Path.bind(null, params);
  const relSubpoint: Point = [0, 0];
  const pathBase: Point = [0, 0];
  let prev: PathDataItem = {
    command: "M",
    args: [],
    base: [0, 0],
    coords: [0, 0],
  };
  let prevQControlPoint: Point | undefined;

  path = path.filter((item, index, currentPath) => {
    const qControlPoint = prevQControlPoint;

    let command = item.command;
    let data = item.args;
    let next = currentPath[index + 1];

    if (command !== "Z" && command !== "z") {
      let sdata = data;
      let circle: Circle | undefined;

      if (command === "s") {
        sdata = [0, 0].concat(data);

        const pdata = prev.args;
        const n = pdata.length;
        sdata[0] = pdata[n - 2] - pdata[n - 4];
        sdata[1] = pdata[n - 1] - pdata[n - 3];
      }

      if (params.makeArcs && (command === "c" || command === "s") && isConvex(sdata) && (circle = findCircle(sdata))) {
        const r = roundData([circle.radius])[0];
        let angle = findArcAngle(sdata, circle);
        const sweep = sdata[5] * sdata[0] - sdata[4] * sdata[1] > 0 ? 1 : 0;
        let arc: PathDataItem = {
          command: "a",
          args: [r, r, 0, 0, sweep, sdata[4], sdata[5]],
          coords: item.coords ? ([item.coords[0], item.coords[1]] as Point) : undefined,
          base: item.base,
        };
        const output: PathDataItem[] = [arc];
        const relCenter: Point = [circle.center[0] - sdata[4], circle.center[1] - sdata[5]];
        const relCircle: Circle = { center: relCenter, radius: circle.radius };
        const arcCurves: PathDataItem[] = [item];
        let hasPrev = 0;
        let suffix = "";
        let nextLonghand: PathDataItem | undefined;
        let j = index;

        if (
          (prev.command === "c" && isConvex(prev.args) && isArcPrev(prev.args, circle)) ||
          (prev.command === "a" && prev.sdata && isArcPrev(prev.sdata, circle))
        ) {
          arcCurves.unshift(prev);
          arc.base = prev.base;
          if (arc.coords && arc.base) {
            arc.args[5] = arc.coords[0] - arc.base[0];
            arc.args[6] = arc.coords[1] - arc.base[1];
          }
          const prevData = prev.command === "a" ? prev.sdata! : prev.args;
          const prevAngle = findArcAngle(prevData, {
            center: [prevData[4] + circle.center[0], prevData[5] + circle.center[1]],
            radius: circle.radius,
          });
          angle += prevAngle;
          if (angle > Math.PI) {
            arc.args[3] = 1;
          }
          hasPrev = 1;
        }

        while ((next = currentPath[++j]) && (next.command === "c" || next.command === "s")) {
          let nextData = next.args;
          if (next.command === "s") {
            nextLonghand = makeLonghand({ command: "s", args: next.args.slice() }, currentPath[j - 1].args);
            nextData = nextLonghand.args;
            nextLonghand.args = nextData.slice(0, 2);
            suffix = stringify([nextLonghand]);
          }
          if (isConvex(nextData) && isArc(nextData, relCircle)) {
            angle += findArcAngle(nextData, relCircle);
            if (angle - 2 * Math.PI > 1e-3) {
              break;
            }
            if (angle > Math.PI) {
              arc.args[3] = 1;
            }
            arcCurves.push(next);
            if (2 * Math.PI - angle > 1e-3) {
              arc.coords = next.coords;
              if (arc.base && arc.coords) {
                arc.args[5] = arc.coords[0] - arc.base[0];
                arc.args[6] = arc.coords[1] - arc.base[1];
              }
            } else {
              arc.args[5] = 2 * (relCircle.center[0] - nextData[4]);
              arc.args[6] = 2 * (relCircle.center[1] - nextData[5]);
              arc.coords = [(arc.base?.[0] ?? 0) + arc.args[5], (arc.base?.[1] ?? 0) + arc.args[6]];
              arc = {
                command: "a",
                args: [
                  r,
                  r,
                  0,
                  0,
                  sweep,
                  (next.coords?.[0] ?? 0) - (arc.coords?.[0] ?? 0),
                  (next.coords?.[1] ?? 0) - (arc.coords?.[1] ?? 0),
                ],
                coords: next.coords,
                base: arc.coords,
              };
              output.push(arc);
              j += 1;
              break;
            }
            relCenter[0] -= nextData[4];
            relCenter[1] -= nextData[5];
          } else {
            break;
          }
        }

        if ((stringify(output) + suffix).length < stringify(arcCurves).length) {
          if (currentPath[j] && currentPath[j].command === "s") {
            makeLonghand(currentPath[j], currentPath[j - 1].args);
          }
          if (hasPrev) {
            const prevArc = output.shift();
            if (!prevArc) {
              return false;
            }
            roundData(prevArc.args);
            relSubpoint[0] += prevArc.args[5] - prev.args[prev.args.length - 2];
            relSubpoint[1] += prevArc.args[6] - prev.args[prev.args.length - 1];
            prev.command = "a";
            prev.args = prevArc.args;
            item.base = prev.coords = prevArc.coords;
          }
          const nextArc = output.shift();
          if (!nextArc) {
            return false;
          }
          arc = nextArc;
          if (arcCurves.length === 1) {
            item.sdata = sdata.slice();
          } else if (arcCurves.length - 1 - hasPrev > 0) {
            currentPath.splice(index + 1, arcCurves.length - 1 - hasPrev, ...output);
          }
          command = "a";
          data = arc.args;
          item.coords = arc.coords;
        }
      }

      if (precision !== false) {
        if (
          command === "m" ||
          command === "l" ||
          command === "t" ||
          command === "q" ||
          command === "s" ||
          command === "c"
        ) {
          for (let i = data.length; i--; ) {
            data[i] += (item.base?.[i % 2] ?? 0) - relSubpoint[i % 2];
          }
        } else if (command === "h") {
          data[0] += (item.base?.[0] ?? 0) - relSubpoint[0];
        } else if (command === "v") {
          data[0] += (item.base?.[1] ?? 0) - relSubpoint[1];
        } else if (command === "a") {
          data[5] += (item.base?.[0] ?? 0) - relSubpoint[0];
          data[6] += (item.base?.[1] ?? 0) - relSubpoint[1];
        }
        roundData(data);

        if (command === "h") {
          relSubpoint[0] += data[0];
        } else if (command === "v") {
          relSubpoint[1] += data[0];
        } else {
          relSubpoint[0] += data[data.length - 2];
          relSubpoint[1] += data[data.length - 1];
        }
        roundData(relSubpoint);

        if (command === "M" || command === "m") {
          pathBase[0] = relSubpoint[0];
          pathBase[1] = relSubpoint[1];
        }
      }

      const sagitta = command === "a" ? calculateSagitta(data) : undefined;
      if (params.smartArcRounding && sagitta !== undefined && precision) {
        for (let precisionNew = precision; precisionNew >= 0; precisionNew -= 1) {
          const radius = toFixed(data[0], precisionNew);
          const sagittaNew = calculateSagitta([radius, radius, ...data.slice(2)]);
          if (sagittaNew !== undefined && Math.abs(sagitta - sagittaNew) < error) {
            data[0] = radius;
            data[1] = radius;
          } else {
            break;
          }
        }
      }

      if (params.straightCurves) {
        if ((command === "c" && isCurveStraightLine(data)) || (command === "s" && isCurveStraightLine(sdata))) {
          if (next && next.command === "s") {
            makeLonghand(next, data);
          }
          command = "l";
          data = data.slice(-2);
        } else if (command === "q" && isCurveStraightLine(data)) {
          if (next && next.command === "t") {
            makeLonghand(next, data);
          }
          command = "l";
          data = data.slice(-2);
        } else if (command === "t" && prev.command !== "q" && prev.command !== "t") {
          command = "l";
          data = data.slice(-2);
        } else if (command === "a" && (data[0] === 0 || data[1] === 0 || (sagitta !== undefined && sagitta < error))) {
          command = "l";
          data = data.slice(-2);
        }
      }

      if (params.convertToQ && command === "c") {
        const x1 = 0.75 * ((item.base?.[0] ?? 0) + data[0]) - 0.25 * (item.base?.[0] ?? 0);
        const x2 = 0.75 * ((item.base?.[0] ?? 0) + data[2]) - 0.25 * ((item.base?.[0] ?? 0) + data[4]);
        if (Math.abs(x1 - x2) < error * 2) {
          const y1 = 0.75 * ((item.base?.[1] ?? 0) + data[1]) - 0.25 * (item.base?.[1] ?? 0);
          const y2 = 0.75 * ((item.base?.[1] ?? 0) + data[3]) - 0.25 * ((item.base?.[1] ?? 0) + data[5]);
          if (Math.abs(y1 - y2) < error * 2) {
            const newData = data.slice();
            newData.splice(0, 4, x1 + x2 - (item.base?.[0] ?? 0), y1 + y2 - (item.base?.[1] ?? 0));
            roundData(newData);
            const originalLength = cleanupOutData(data, params).length;
            const newLength = cleanupOutData(newData, params).length;
            if (newLength < originalLength) {
              command = "q";
              data = newData;
              if (next && next.command === "s") {
                makeLonghand(next, data);
              }
            }
          }
        }
      }

      if (params.lineShorthands && command === "l") {
        if (data[1] === 0) {
          command = "h";
          data.pop();
        } else if (data[0] === 0) {
          command = "v";
          data.shift();
        }
      }

      if (
        params.collapseRepeated &&
        hasMarkerMid === false &&
        (command === "m" || command === "h" || command === "v") &&
        prev.command &&
        command === prev.command.toLowerCase() &&
        ((command !== "h" && command !== "v") || prev.args[0] >= 0 === data[0] >= 0)
      ) {
        prev.args[0] += data[0];
        if (command !== "h" && command !== "v") {
          prev.args[1] += data[1];
        }
        prev.coords = item.coords;
        currentPath[index] = prev;
        return false;
      }

      if (params.curveSmoothShorthands && prev.command) {
        if (command === "c") {
          if (
            prev.command === "c" &&
            Math.abs(data[0] - -(prev.args[2] - prev.args[4])) < error &&
            Math.abs(data[1] - -(prev.args[3] - prev.args[5])) < error
          ) {
            command = "s";
            data = data.slice(2);
          } else if (
            prev.command === "s" &&
            Math.abs(data[0] - -(prev.args[0] - prev.args[2])) < error &&
            Math.abs(data[1] - -(prev.args[1] - prev.args[3])) < error
          ) {
            command = "s";
            data = data.slice(2);
          } else if (
            prev.command !== "c" &&
            prev.command !== "s" &&
            Math.abs(data[0]) < error &&
            Math.abs(data[1]) < error
          ) {
            command = "s";
            data = data.slice(2);
          }
        } else if (command === "q") {
          if (
            prev.command === "q" &&
            Math.abs(data[0] - (prev.args[2] - prev.args[0])) < error &&
            Math.abs(data[1] - (prev.args[3] - prev.args[1])) < error
          ) {
            command = "t";
            data = data.slice(2);
          } else if (prev.command === "t") {
            const predictedControlPoint = reflectPoint(qControlPoint ?? [0, 0], item.base ?? [0, 0]);
            const realControlPoint: Point = [data[0] + (item.base?.[0] ?? 0), data[1] + (item.base?.[1] ?? 0)];
            if (
              Math.abs(predictedControlPoint[0] - realControlPoint[0]) < error &&
              Math.abs(predictedControlPoint[1] - realControlPoint[1]) < error
            ) {
              command = "t";
              data = data.slice(2);
            }
          }
        }
      }

      if (params.removeUseless && !maybeHasStrokeAndLinecap) {
        if (
          (command === "l" ||
            command === "h" ||
            command === "v" ||
            command === "q" ||
            command === "t" ||
            command === "c" ||
            command === "s") &&
          data.every(i => i === 0)
        ) {
          currentPath[index] = prev;
          return false;
        }
        if (command === "a" && data[5] === 0 && data[6] === 0) {
          currentPath[index] = prev;
          return false;
        }
      }

      if (
        params.convertToZ &&
        (isSafeToUseZ || next?.command === "Z" || next?.command === "z") &&
        (command === "l" || command === "h" || command === "v")
      ) {
        if (
          Math.abs(pathBase[0] - (item.coords?.[0] ?? 0)) < error &&
          Math.abs(pathBase[1] - (item.coords?.[1] ?? 0)) < error
        ) {
          command = "z";
          data = [];
        }
      }

      item.command = command;
      item.args = data;
    } else {
      relSubpoint[0] = pathBase[0];
      relSubpoint[1] = pathBase[1];
      if (prev.command === "Z" || prev.command === "z") {
        return false;
      }
    }
    if (
      (command === "Z" || command === "z") &&
      params.removeUseless &&
      isSafeToUseZ &&
      Math.abs((item.base?.[0] ?? 0) - (item.coords?.[0] ?? 0)) < error / 10 &&
      Math.abs((item.base?.[1] ?? 0) - (item.coords?.[1] ?? 0)) < error / 10
    ) {
      return false;
    }

    if (command === "q") {
      prevQControlPoint = [data[0] + (item.base?.[0] ?? 0), data[1] + (item.base?.[1] ?? 0)];
    } else if (command === "t") {
      if (qControlPoint) {
        prevQControlPoint = reflectPoint(qControlPoint, item.base ?? [0, 0]);
      } else {
        prevQControlPoint = item.coords;
      }
    } else {
      prevQControlPoint = undefined;
    }
    prev = item;
    return true;
  });

  return path;
}

/**
 * Writes data in shortest form using absolute or relative coordinates.
 */
function convertToMixed(path: PathDataItem[], params: InternalParams): PathDataItem[] {
  let prev = path[0];

  path = path.filter((item, index) => {
    if (index === 0) {
      return true;
    }
    if (item.command === "Z" || item.command === "z") {
      prev = item;
      return true;
    }

    const command = item.command;
    const data = item.args;
    const adata = data.slice();
    const rdata = data.slice();

    if (
      command === "m" ||
      command === "l" ||
      command === "t" ||
      command === "q" ||
      command === "s" ||
      command === "c"
    ) {
      for (let i = adata.length; i--; ) {
        adata[i] += item.base?.[i % 2] ?? 0;
      }
    } else if (command === "h") {
      adata[0] += item.base?.[0] ?? 0;
    } else if (command === "v") {
      adata[0] += item.base?.[1] ?? 0;
    } else if (command === "a") {
      adata[5] += item.base?.[0] ?? 0;
      adata[6] += item.base?.[1] ?? 0;
    }

    roundData(adata);
    roundData(rdata);

    const absoluteDataStr = cleanupOutData(adata, params);
    const relativeDataStr = cleanupOutData(rdata, params);

    if (
      params.forceAbsolutePath ||
      (absoluteDataStr.length < relativeDataStr.length &&
        !(
          params.negativeExtraSpace &&
          command === prev.command &&
          prev.command.charCodeAt(0) > 96 &&
          absoluteDataStr.length === relativeDataStr.length - 1 &&
          (data[0] < 0 ||
            (Math.floor(data[0]) === 0 && !Number.isInteger(data[0]) && (prev.args[prev.args.length - 1] ?? 0) % 1))
        ))
    ) {
      item.command = command.toUpperCase() as PathDataCommand;
      item.args = adata;
    }

    prev = item;
    return true;
  });

  return path;
}

interface Circle {
  center: Point;
  radius: number;
}

function data2Path(params: InternalParams, pathData: PathDataItem[]): string {
  return pathData.reduce((pathString, item) => {
    let strData = "";
    if (item.args) {
      strData = cleanupOutData(roundData(item.args.slice()), params);
    }
    return pathString + item.command + strData;
  }, "");
}

const stringifyOptimizedPath = (pathData: PathDataItem[], params: InternalParams): string => {
  return stringifyPathDataInternal({
    pathData,
    precision: params.floatPrecision === false ? undefined : params.floatPrecision,
    disableSpaceAfterFlags: params.noSpaceAfterFlags,
  });
};

interface StringifyPathDataOptions {
  pathData: PathDataItem[];
  precision?: number;
  disableSpaceAfterFlags?: boolean;
}

const stringifyPathDataInternal = ({
  pathData,
  precision,
  disableSpaceAfterFlags,
}: StringifyPathDataOptions): string => {
  if (pathData.length === 0) {
    return "";
  }
  if (pathData.length === 1) {
    const { command, args } = pathData[0];
    return command + stringifyArgs(command, args, precision, disableSpaceAfterFlags);
  }

  let result = "";
  let prev = {
    command: pathData[0].command,
    args: pathData[0].args.slice(),
  };

  if (pathData[1].command === "L") {
    prev.command = "M";
  } else if (pathData[1].command === "l") {
    prev.command = "m";
  }

  for (let i = 1; i < pathData.length; i += 1) {
    const { command, args } = pathData[i];
    if (
      (prev.command === command && prev.command !== "M" && prev.command !== "m") ||
      (prev.command === "M" && command === "L") ||
      (prev.command === "m" && command === "l")
    ) {
      prev.args = [...prev.args, ...args];
      if (i === pathData.length - 1) {
        result += prev.command + stringifyArgs(prev.command, prev.args, precision, disableSpaceAfterFlags);
      }
    } else {
      result += prev.command + stringifyArgs(prev.command, prev.args, precision, disableSpaceAfterFlags);

      if (i === pathData.length - 1) {
        result += command + stringifyArgs(command, args, precision, disableSpaceAfterFlags);
      } else {
        prev = {
          command,
          args: args.slice(),
        };
      }
    }
  }

  return result;
};

const roundAndStringify = (value: number, precision?: number) => {
  let number = value;
  if (precision != null) {
    number = toFixed(number, precision);
  }
  return {
    roundedStr: removeLeadingZero(number),
    rounded: number,
  };
};

const stringifyArgs = (
  command: PathDataCommand,
  args: ReadonlyArray<number>,
  precision?: number,
  disableSpaceAfterFlags?: boolean,
): string => {
  let result = "";
  let previous: number | undefined;

  for (let i = 0; i < args.length; i += 1) {
    const { roundedStr, rounded } = roundAndStringify(args[i], precision);
    if (disableSpaceAfterFlags && (command === "A" || command === "a") && (i % 7 === 4 || i % 7 === 5)) {
      result += roundedStr;
    } else if (i === 0 || rounded < 0) {
      result += roundedStr;
    } else if (previous != null && !Number.isInteger(previous) && roundedStr[0] != null && !isDigit(roundedStr[0])) {
      result += roundedStr;
    } else {
      result += ` ${roundedStr}`;
    }
    previous = rounded;
  }

  return result;
};

function isConvex(data: ReadonlyArray<number>): boolean {
  const center = getIntersection([0, 0, data[2], data[3], data[0], data[1], data[4], data[5]]);

  return (
    center != null &&
    data[2] < center[0] === center[0] < 0 &&
    data[3] < center[1] === center[1] < 0 &&
    data[4] < center[0] === center[0] < data[0] &&
    data[5] < center[1] === center[1] < data[1]
  );
}

function getIntersection(coords: ReadonlyArray<number>): Point | undefined {
  const a1 = coords[1] - coords[3];
  const b1 = coords[2] - coords[0];
  const c1 = coords[0] * coords[3] - coords[2] * coords[1];
  const a2 = coords[5] - coords[7];
  const b2 = coords[6] - coords[4];
  const c2 = coords[4] * coords[7] - coords[5] * coords[6];
  const denom = a1 * b2 - a2 * b1;

  if (!denom) {
    return undefined;
  }

  const cross: Point = [(b1 * c2 - b2 * c1) / denom, (a1 * c2 - a2 * c1) / -denom];
  if (!Number.isNaN(cross[0]) && !Number.isNaN(cross[1]) && Number.isFinite(cross[0]) && Number.isFinite(cross[1])) {
    return cross;
  }
  return undefined;
}

function strongRound(data: number[]): number[] {
  const precisionNum = precision || 0;
  for (let i = data.length; i-- > 0; ) {
    const fixed = toFixed(data[i], precisionNum);
    if (fixed !== data[i]) {
      const rounded = toFixed(data[i], precisionNum - 1);
      data[i] = toFixed(Math.abs(rounded - data[i]), precisionNum + 1) >= error ? fixed : rounded;
    }
  }
  return data;
}

function round(data: number[]): number[] {
  for (let i = data.length; i-- > 0; ) {
    data[i] = Math.round(data[i]);
  }
  return data;
}

function isCurveStraightLine(data: ReadonlyArray<number>): boolean {
  let i = data.length - 2;
  const a = -data[i + 1];
  const b = data[i];
  const d = 1 / (a * a + b * b);

  if (i <= 1 || !isFinite(d)) {
    return false;
  }

  while ((i -= 2) >= 0) {
    if (Math.sqrt((a * data[i] + b * data[i + 1]) ** 2 * d) > error) {
      return false;
    }
  }

  return true;
}

function calculateSagitta(data: ReadonlyArray<number>): number | undefined {
  if (data[3] === 1) {
    return undefined;
  }
  const [rx, ry] = data;
  if (Math.abs(rx - ry) > error) {
    return undefined;
  }
  const chord = Math.hypot(data[5], data[6]);
  if (chord > rx * 2) {
    return undefined;
  }
  return rx - Math.sqrt(rx ** 2 - 0.25 * chord ** 2);
}

function makeLonghand(item: PathDataItem, data: ReadonlyArray<number>): PathDataItem {
  switch (item.command) {
    case "s":
      item.command = "c";
      break;
    case "t":
      item.command = "q";
      break;
  }
  item.args.unshift(data[data.length - 2] - data[data.length - 4], data[data.length - 1] - data[data.length - 3]);
  return item;
}

function getDistance(point1: Point, point2: Point): number {
  return Math.hypot(point1[0] - point2[0], point1[1] - point2[1]);
}

function reflectPoint(controlPoint: Point, base: Point): Point {
  return [2 * base[0] - controlPoint[0], 2 * base[1] - controlPoint[1]];
}

function getCubicBezierPoint(curve: ReadonlyArray<number>, t: number): Point {
  const sqrT = t * t;
  const cubT = sqrT * t;
  const mt = 1 - t;
  const sqrMt = mt * mt;

  return [
    3 * sqrMt * t * curve[0] + 3 * mt * sqrT * curve[2] + cubT * curve[4],
    3 * sqrMt * t * curve[1] + 3 * mt * sqrT * curve[3] + cubT * curve[5],
  ];
}

function findCircle(curve: ReadonlyArray<number>): Circle | undefined {
  const midPoint = getCubicBezierPoint(curve, 1 / 2);
  const m1: Point = [midPoint[0] / 2, midPoint[1] / 2];
  const m2: Point = [(midPoint[0] + curve[4]) / 2, (midPoint[1] + curve[5]) / 2];
  const center = getIntersection([
    m1[0],
    m1[1],
    m1[0] + m1[1],
    m1[1] - m1[0],
    m2[0],
    m2[1],
    m2[0] + (m2[1] - midPoint[1]),
    m2[1] - (m2[0] - midPoint[0]),
  ]);
  if (!center) {
    return undefined;
  }
  const radius = getDistance([0, 0], center);
  const tolerance = Math.min(arcThreshold * error, (arcTolerance * radius) / 100);

  if (
    radius < 1e15 &&
    [1 / 4, 3 / 4].every(point => {
      return Math.abs(getDistance(getCubicBezierPoint(curve, point), center) - radius) <= tolerance;
    })
  ) {
    return { center, radius };
  }
  return undefined;
}

function isArc(curve: ReadonlyArray<number>, circle: Circle): boolean {
  const tolerance = Math.min(arcThreshold * error, (arcTolerance * circle.radius) / 100);

  return [0, 1 / 4, 1 / 2, 3 / 4, 1].every(point => {
    return Math.abs(getDistance(getCubicBezierPoint(curve, point), circle.center) - circle.radius) <= tolerance;
  });
}

function isArcPrev(curve: ReadonlyArray<number>, circle: Circle): boolean {
  return isArc(curve, {
    center: [circle.center[0] + curve[4], circle.center[1] + curve[5]],
    radius: circle.radius,
  });
}

function findArcAngle(curve: ReadonlyArray<number>, relCircle: Circle): number {
  const x1 = -relCircle.center[0];
  const y1 = -relCircle.center[1];
  const x2 = curve[4] - relCircle.center[0];
  const y2 = curve[5] - relCircle.center[1];

  return Math.acos((x1 * x2 + y1 * y2) / Math.sqrt((x1 * x1 + y1 * y1) * (x2 * x2 + y2 * y2)));
}

const argsCountPerCommand: Record<PathDataCommand, number> = {
  M: 2,
  m: 2,
  Z: 0,
  z: 0,
  L: 2,
  l: 2,
  H: 1,
  h: 1,
  V: 1,
  v: 1,
  C: 6,
  c: 6,
  S: 4,
  s: 4,
  Q: 4,
  q: 4,
  T: 2,
  t: 2,
  A: 7,
  a: 7,
};

const isCommand = (c: string): c is PathDataCommand => {
  return Object.prototype.hasOwnProperty.call(argsCountPerCommand, c);
};

const isWhiteSpace = (c: string): boolean => {
  return c === " " || c === "\t" || c === "\r" || c === "\n";
};

const isDigit = (c: string): boolean => {
  const codePoint = c.codePointAt(0);
  if (codePoint == null) {
    return false;
  }
  return 48 <= codePoint && codePoint <= 57;
};

const readNumber = (string: string, cursor: number): [number, number | null] => {
  let i = cursor;
  let value = "";
  let state: ReadNumberState = "none";
  for (; i < string.length; i += 1) {
    const c = string[i];
    if (c === "+" || c === "-") {
      if (state === "none") {
        state = "sign";
        value += c;
        continue;
      }
      if (state === "e") {
        state = "exponent_sign";
        value += c;
        continue;
      }
    }
    if (isDigit(c)) {
      if (state === "none" || state === "sign" || state === "whole") {
        state = "whole";
        value += c;
        continue;
      }
      if (state === "decimal_point" || state === "decimal") {
        state = "decimal";
        value += c;
        continue;
      }
      if (state === "e" || state === "exponent_sign" || state === "exponent") {
        state = "exponent";
        value += c;
        continue;
      }
    }
    if (c === ".") {
      if (state === "none" || state === "sign" || state === "whole") {
        state = "decimal_point";
        value += c;
        continue;
      }
    }
    if (c === "E" || c === "e") {
      if (state === "whole" || state === "decimal_point" || state === "decimal") {
        state = "e";
        value += c;
        continue;
      }
    }
    break;
  }
  const number = Number.parseFloat(value);
  if (Number.isNaN(number)) {
    return [cursor, null];
  }
  return [i - 1, number];
};

function parsePathData(input: string): PathDataItem[] {
  const pathData: PathDataItem[] = [];
  let command: PathDataCommand | null = null;
  let args: number[] = [];
  let argsCount = 0;
  let canHaveComma = false;
  let hadComma = false;
  for (let i = 0; i < input.length; i += 1) {
    const c = input.charAt(i);
    if (isWhiteSpace(c)) {
      continue;
    }
    if (canHaveComma && c === ",") {
      if (hadComma) {
        break;
      }
      hadComma = true;
      continue;
    }
    if (isCommand(c)) {
      if (hadComma) {
        return pathData;
      }
      if (command == null) {
        if (c !== "M" && c !== "m") {
          return pathData;
        }
      } else if (args.length !== 0) {
        return pathData;
      }
      command = c;
      args = [];
      argsCount = argsCountPerCommand[command];
      canHaveComma = false;
      if (argsCount === 0) {
        pathData.push({ command, args: [] });
      }
      continue;
    }
    if (command == null) {
      return pathData;
    }
    let newCursor = i;
    let number = null;
    if (command === "A" || command === "a") {
      const position = args.length;
      if (position === 0 || position === 1) {
        if (c !== "+" && c !== "-") {
          [newCursor, number] = readNumber(input, i);
        }
      } else if (position === 3 || position === 4) {
        if (c === "0" || c === "1") {
          number = Number.parseFloat(c);
        }
      } else {
        [newCursor, number] = readNumber(input, i);
      }
    } else {
      [newCursor, number] = readNumber(input, i);
    }
    if (number == null) {
      return pathData;
    }
    args.push(number);
    hadComma = false;
    canHaveComma = true;
    if (args.length === argsCount) {
      pathData.push({ command, args });
      args = [];
      if (command === "M") {
        command = "L";
      } else if (command === "m") {
        command = "l";
      }
    }
    i = newCursor;
  }
  return pathData;
}

const cleanupOutData = (data: ReadonlyArray<number>, params: InternalParams, command?: PathDataCommand): string => {
  let str = "";
  let delimiter: string;
  let prev: number | undefined;

  data.forEach((item, i) => {
    delimiter = " ";
    if (i === 0) {
      delimiter = "";
    }
    if (params.noSpaceAfterFlags && (command === "A" || command === "a")) {
      const pos = i % 7;
      if (pos === 4 || pos === 5) {
        delimiter = "";
      }
    }
    const itemStr = params.leadingZero ? removeLeadingZero(item) : item.toString();

    if (
      params.negativeExtraSpace &&
      delimiter !== "" &&
      (item < 0 || (itemStr.charAt(0) === "." && (prev ?? 0) % 1 !== 0))
    ) {
      delimiter = "";
    }
    prev = item;
    str += delimiter + itemStr;
  });
  return str;
};

const removeLeadingZero = (value: number): string => {
  const strValue = value.toString();

  if (0 < value && value < 1 && strValue.startsWith("0")) {
    return strValue.slice(1);
  }

  if (-1 < value && value < 0 && strValue[1] === "0") {
    return strValue[0] + strValue.slice(2);
  }

  return strValue;
};

const toFixed = (num: number, precisionValue: number): number => {
  const pow = 10 ** precisionValue;
  return Math.round(num * pow) / pow;
};
